import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { BaseHttpService } from './base-http.service';

export interface PaginatorSettings {
  pageIndex: number;
  pageSize: number;
}

/**
 * User Preferences Service
 * Kezeli a felhasználói preferenciákat (paginator beállítások, szűrők, stb.)
 */
@Injectable({
  providedIn: 'root'
})
export class UserPreferencesService extends BaseHttpService {

  /** Valós idejű értesítő az időrács felbontás változásáról */
  private slotDurationChange$ = new BehaviorSubject<string>('00:15:00');
  readonly slotDuration$ = this.slotDurationChange$.asObservable();

  /** Valós idejű értesítő a naptár kolléga-kiválasztás változásáról */
  private calendarSelectionChanged = new Subject<number[]>();
  readonly calendarSelection$ = this.calendarSelectionChanged.asObservable();
  
  /**
   * Adott oldal paginator beállításainak lekérdezése
   * @param pageName Oldal azonosító (pl. 'call_center_database', 'contracts')
   */
  getPaginatorSettings(pageName: string): Observable<PaginatorSettings> {
    return this.get<PaginatorSettings>(`/auth/me/preferences/paginator/${pageName}`);
  }

  /**
   * Paginator beállítások mentése
   * @param pageName Oldal azonosító
   * @param settings Paginator beállítások (pageIndex, pageSize)
   */
  savePaginatorSettings(pageName: string, settings: PaginatorSettings): Observable<any> {
    return this.put(`/auth/me/preferences/paginator/${pageName}`, settings);
  }

  /**
   * Paginator beállítások törlése
   * @param pageName Oldal azonosító
   */
  deletePaginatorSettings(pageName: string): Observable<any> {
    return this.delete(`/auth/me/preferences/paginator/${pageName}`);
  }

  /**
   * Összes felhasználói preferencia lekérdezése
   */
  getAllPreferences(): Observable<any> {
    return this.get<any>('/auth/me/preferences');
  }

  // =========================================================================
  // STICKY FILTER BEÁLLÍTÁSOK
  // =========================================================================

  /**
   * Adott oldal sticky filter beállításának lekérdezése
   * @param pageName Oldal azonosító (pl. 'contracts', 'call_center_database')
   */
  getStickyFilterSetting(pageName: string): Observable<{ sticky: boolean }> {
    return this.get<{ sticky: boolean }>(`/auth/me/preferences/sticky-filter/${pageName}`);
  }

  /**
   * Sticky filter beállítás mentése
   * @param pageName Oldal azonosító
   * @param sticky Rögzített szűrő állapot (true/false)
   */
  saveStickyFilterSetting(pageName: string, sticky: boolean): Observable<any> {
    return this.put(`/auth/me/preferences/sticky-filter/${pageName}`, { sticky });
  }

  // =========================================================================
  // NAPTÁR KIVÁLASZTÁS BEÁLLÍTÁSOK
  // =========================================================================

  /**
   * Naptár kolléga-kiválasztás lekérdezése
   */
  getCalendarSelection(): Observable<{ calendars: number[] }> {
    return this.get<{ calendars: number[] }>('/auth/me/preferences/calendars');
  }

  /**
   * Naptár kolléga-kiválasztás mentése
   * @param calendars Kiválasztott user ID-k tömbje
   */
  saveCalendarSelection(calendars: number[]): Observable<any> {
    return this.put('/auth/me/preferences/calendars', { calendars }).pipe(
      tap(() => this.calendarSelectionChanged.next(calendars))
    );
  }

  // =========================================================================
  // NAPTÁR IDŐRÁCS FELBONTÁS BEÁLLÍTÁSOK
  // =========================================================================

  /**
   * Időrács felbontás beállítás lekérdezése
   */
  getCalendarSlotDuration(): Observable<{ slot_duration: string }> {
    return this.get<{ slot_duration: string }>('/auth/me/preferences/calendar-slot-duration');
  }

  /**
   * Időrács felbontás beállítás mentése
   * @param slotDuration Időrács felbontás értéke ("00:15:00" | "00:20:00")
   */
  saveCalendarSlotDuration(slotDuration: string): Observable<any> {
    return this.put('/auth/me/preferences/calendar-slot-duration', { slot_duration: slotDuration }).pipe(
      tap(() => this.slotDurationChange$.next(slotDuration))
    );
  }

  /** Helyi értesítés küldése (pl. betöltéskor a kezdeti érték beállítására) */
  notifySlotDurationLoaded(slotDuration: string): void {
    this.slotDurationChange$.next(slotDuration);
  }

  /** Helyi értesítés küldése a naptár kiválasztás változásáról (pl. betöltéskor) */
  notifyCalendarSelectionLoaded(calendars: number[]): void {
    this.calendarSelectionChanged.next(calendars);
  }
}
